import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import EventAvailable from '@mui/icons-material/EventAvailable'
import CalendarToday from '@mui/icons-material/CalendarToday'
import EventRepeat from '@mui/icons-material/EventRepeat'
import LocationOn from '@mui/icons-material/LocationOn'
import WatchLater from '@mui/icons-material/WatchLater'
import { Calendar, Event } from './types'
import AlreadyRescheduledModal from './AlreadyRescheduledModal'
import TooCloseToEventTimeModal from './TooCloseToEventTimeModal'
import RescheduleWarningModal from './RescheduleWarningModal'
import VendorDetails from './components/VendorDetails'
import PointOfContact from './components/PointOfContact'

dayjs.extend(timezone)

const TOO_CLOSE_TIME = 24 * 60 * 60 * 1000

interface ScheduledProps {
  calendar: Calendar
  event: Event
  setRescheduling: (rescheduling: boolean) => void
  reschedulingRequestSent: boolean
}

const Scheduled: FC<ScheduledProps> = ({ calendar, event, setRescheduling, reschedulingRequestSent }) => {
  const [scheduleRequestCount, setScheduleRequestCount] = useState(0)
  const [alreadyRescheduledModalOpen, setAlreadyRescheduledModalOpen] = useState(false)
  const [tooCloseToEventModal, setTooCloseToEventModal] = useState(false)
  const [rescheduleWarningModalOpen, setRescheduleWarningModalOpen] = useState(false)
  const [rescheduleWarningConfirmed, setRescheduleWarningConfirmed] = useState(false)

  const tooCloseToEvent = useMemo(() => {
    if (!event) return false
    return dayjs(event.start_time).tz(calendar.property.time_zone).diff() <= TOO_CLOSE_TIME
  }, [event, calendar])

  useEffect(() => {
    if (!window.location.search.includes('reschedule')) return
    if (event?.status == 'rescheduled' || calendar.homeowner_rescheduled || calendar.vendor_rescheduled) {
      setAlreadyRescheduledModalOpen(true)
    } else if (tooCloseToEvent) {
      setTooCloseToEventModal(true)
    } else if (!rescheduleWarningConfirmed && event?.status != 'unscheduled') {
      setRescheduleWarningModalOpen(true)
    }
  }, [
    scheduleRequestCount,
    event,
    tooCloseToEvent,
    rescheduleWarningConfirmed,
    calendar.homeowner_rescheduled,
    calendar.vendor_rescheduled,
  ])

  const handleRescheduleClick = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search)
    urlParams.set('reschedule', 'true')
    const newUrl = [window.location.href.replace(/\?.*/, ''), urlParams.toString()].filter(Boolean).join('?')
    window.history.replaceState(null, '', newUrl)
    setScheduleRequestCount((oldScheduleRequestCount) => oldScheduleRequestCount + 1)
  }, [])

  const handleRescheduleWarningConfirmed = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search)
    urlParams.delete('reschedule')
    const newUrl = [window.location.href.replace(/\?.*/, ''), urlParams.toString()].filter(Boolean).join('?')
    window.history.replaceState(null, '', newUrl)
    setRescheduleWarningModalOpen(false)
    setRescheduleWarningConfirmed(true)
    setRescheduling(true)
  }, [setRescheduling])

  if (!event.contact) return null

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      {reschedulingRequestSent || event.status == 'unscheduled' ? (
        <div className="tw:p-6 tw:pt-8 tw:flex tw:flex-col tw:items-center tw:bg-white tw:rounded-5xl tw:border tw:border-neutrals-300 tw:text-center">
          <div className="tw:bg-green-100 tw:rounded-full tw:p-6">
            <EventRepeat className="tw:text-green-700 tw:!size-12" />
          </div>
          <h2 className="tw:text-lg tw:font-bold tw:mt-6 tw:leading-tight">
            {`Your availability was updated & request was sent to ${event.contact.company_name} to reschedule`}
          </h2>
          <p className="tw:mt-1 tw:leading-tight">
            {"They will now choose their preferred times, and we'll confirm your site visit soon."}
          </p>
        </div>
      ) : (
        <div className="tw:p-6 tw:pt-8 tw:flex tw:flex-col tw:items-center tw:bg-white tw:rounded-5xl tw:border tw:border-neutrals-300">
          <div className="tw:bg-green-100 tw:rounded-full tw:p-6">
            <EventAvailable className="tw:text-green-700 tw:!size-12" />
          </div>
          <h2 className="tw:text-lg tw:font-bold tw:mt-6">{'Site visit scheduled'}</h2>
          <hr className="tw:my-4 tw:w-full" />
          <div className="tw:w-full">
            <div className="tw:flex tw:items-center tw:gap-4 tw:my-3">
              <div className="tw:bg-neutrals-200 tw:p-1.5 tw:rounded-full tw:flex tw:items-center tw:justify-center">
                <CalendarToday className="tw:!size-5" />
              </div>
              <div className="tw:grow tw:font-medium">
                {dayjs(event.start_time).tz(calendar.property.time_zone).format('ddd, D MMM')}
              </div>
            </div>
            <div className="tw:flex tw:items-center tw:gap-4 tw:my-3">
              <div className="tw:bg-neutrals-200 tw:p-1.5 tw:rounded-full tw:flex tw:items-center tw:justify-center">
                <WatchLater className="tw:!size-5" />
              </div>
              <div className="tw:grow">
                <div>
                  {dayjs(event.start_time).tz(calendar.property.time_zone).format('h:mm')}
                  {' - '}
                  {dayjs(event.end_time).tz(calendar.property.time_zone).format('h:mm A')}
                </div>
                <div className="tw:text-tertiary-black">{'Pacific Time'}</div>
              </div>
            </div>
            <div className="tw:flex tw:items-center tw:gap-4 tw:my-3">
              <div className="tw:bg-neutrals-200 tw:p-1.5 tw:rounded-full tw:flex tw:items-center tw:justify-center">
                <LocationOn className="tw:!size-5" />
              </div>
              <div className="tw:grow">
                <div>{calendar.property.delivery_line_1}</div>
                <div className="tw:text-tertiary-black">
                  {[calendar.property.city, calendar.property.state, calendar.property.zip5].filter(Boolean).join(', ')}
                </div>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleRescheduleClick}
            className="tw:rounded-full tw:p-3 tw:bg-neutrals-200 tw:text-xs tw:font-bold tw:w-full tw:mt-6"
          >
            {'Reschedule'}
          </button>
        </div>
      )}
      <VendorDetails event={event} />
      <PointOfContact calendar={calendar} />
      <AlreadyRescheduledModal
        open={alreadyRescheduledModalOpen}
        setOpen={setAlreadyRescheduledModalOpen}
        calendar={calendar}
      />
      <TooCloseToEventTimeModal open={tooCloseToEventModal} setOpen={setTooCloseToEventModal} calendar={calendar} />
      <RescheduleWarningModal
        open={rescheduleWarningModalOpen}
        setOpen={setRescheduleWarningModalOpen}
        calendar={calendar}
        event={event}
        onConfirm={handleRescheduleWarningConfirmed}
      />
    </div>
  )
}

export default Scheduled
